import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Typography,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { generateRandomPassword } from '../../../utils/random';

export default function ResetPasswordDialog({ 
  open, 
  onClose, 
  staffMember,
  onPasswordReset,
  loading = false 
}) {
  const { t } = useTranslation(['staff', 'common']);
  const [step, setStep] = useState('confirm'); // 'confirm' or 'success'
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const generatePassword = () => {
    return generateRandomPassword(8, "0123456789");
  };

  const handleClose = () => {
    if (!loading) {
      setStep('confirm');
      setNewPassword('');
      setError('');
      setCopied(false);
      onClose();
    }
  };

  const handleConfirm = async () => {
    const password = generatePassword();
    setError('');
    
    const result = await onPasswordReset(staffMember.staff_member_id, password);
    
    if (result.success) {
      setNewPassword(password);
      setStep('success');
    } else {
      setError(result.error?.message || t('staff:profile.password_reset_failed'));
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {t('staff:profile.reset_password')}
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {step === 'confirm' ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('staff:profile.reset_password_warning', { name: staffMember?.name })}
            </Alert>
            <Typography>
              {t('staff:profile.reset_password_confirmation')}
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('staff:profile.password_reset_success')}
            </Alert>
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              {t('staff:profile.new_temporary_password')}
            </Typography>
            
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: 'grey.100', 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2
              }}
            >
              <Box display="flex" gap={3}>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                  {newPassword.slice(0,2)}
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                  {newPassword.slice(2,4)}
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                  {newPassword.slice(4,6)}
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'monospace', letterSpacing: 1 }}>
                  {newPassword.slice(6,8)}
                </Typography>
              </Box>
              <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'}>
                <ContentCopyIcon />
              </IconButton>
            </Paper>

            <Alert severity="info">
              <Typography variant="body2">
                {t('staff:profile.reset_password_instructions')}
              </Typography>
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'confirm' ? (
          <>
            <Button onClick={handleClose} disabled={loading}>
              {t('common:buttons.cancel')}
            </Button>
            <Button 
              onClick={handleConfirm}
              variant="contained"
              color="warning"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : t('staff:profile.reset_password')}
            </Button>
          </>
        ) : (
          <Button onClick={handleClose} variant="contained">
            {t('common:buttons.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}